/**
 * Vercel API integration for domain management.
 * Uses server-side edge function (manage-vercel-domain) to keep VERCEL_TOKEN secure.
 * Supports subdomains (e.g. myorg.getnos.ai) and custom domains (e.g. donate.example.org).
 */

import { FunctionsHttpError } from "@supabase/supabase-js"
import { supabase } from "@/lib/supabase"

async function extractErrorMessage(
  error: unknown,
  data: { error?: string } | null
): Promise<string> {
  if (data?.error && typeof data.error === "string") return data.error
  if (error instanceof FunctionsHttpError && error.context) {
    try {
      const res = error.context as Response
      const body = await res.clone().json().catch(() => ({})) as { error?: string }
      if (typeof body.error === "string") return body.error
    } catch {
      // ignore
    }
  }
  if (error instanceof Error) return error.message
  return "An unexpected error occurred"
}

export const ROOT_DOMAIN = "getnos.ai"

export interface VercelDomainResult {
  success: boolean
  verified?: boolean
  error?: string
}

function fullDomain(subdomain: string): string {
  const normalized = subdomain.trim().toLowerCase()
  return normalized ? `${normalized}.${ROOT_DOMAIN}` : ROOT_DOMAIN
}

export { fullDomain }

/**
 * Adds a domain to the Vercel project.
 * For subdomains: pass subdomain (e.g. "myorg") -> myorg.getnos.ai
 * For custom domains: pass full domain (e.g. "donate.example.org")
 */
export async function addDomainToVercel(
  subdomainOrDomain: string,
  orgId: string
): Promise<VercelDomainResult> {
  const isFullDomain = subdomainOrDomain.includes(".")
  const body = isFullDomain
    ? { action: "add" as const, domain: subdomainOrDomain.trim().toLowerCase(), org_id: orgId }
    : { action: "add" as const, subdomain: subdomainOrDomain, org_id: orgId }

  const { data, error } = await supabase.functions.invoke("manage-vercel-domain", {
    body,
  })

  if (error) {
    return {
      success: false,
      error: await extractErrorMessage(error, data),
    }
  }

  if (data?.success === true) {
    return { success: true, verified: data.verified ?? false }
  }

  return {
    success: false,
    error: data?.error ?? "Failed to add domain",
  }
}

/**
 * Removes a domain from the Vercel project.
 */
export async function removeDomainFromVercel(
  subdomainOrDomain: string,
  orgId: string
): Promise<VercelDomainResult> {
  const isFullDomain = subdomainOrDomain.includes(".")
  const body = isFullDomain
    ? { action: "remove" as const, domain: subdomainOrDomain.trim().toLowerCase(), org_id: orgId }
    : { action: "remove" as const, subdomain: subdomainOrDomain, org_id: orgId }

  const { data, error } = await supabase.functions.invoke("manage-vercel-domain", {
    body,
  })

  if (error) {
    return {
      success: false,
      error: await extractErrorMessage(error, data),
    }
  }

  if (data?.success === true) {
    return { success: true }
  }

  return {
    success: false,
    error: data?.error ?? "Failed to remove domain",
  }
}

